import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/feature/EmptyState";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { buildEpisodes } from "@/mocks/episodes";
import { formatUpdated, sortAnime } from "@/utils/anime";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import EpisodeList from "@/pages/detail/components/EpisodeList";

const LOADING_MS = 380;

export default function Detail() {
  const { animeId = "" } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [loading, setLoading] = useState(true);
  const [selectedEp, setSelectedEp] = useState(1);
  const [toast, setToast] = useState("");
  const [showAllTags, setShowAllTags] = useState(false);

  const anime = animeList.find((item) => item.id === animeId);

  useEffect(() => {
    setLoading(true);
    setSelectedEp(1);
    const timer = window.setTimeout(() => setLoading(false), LOADING_MS);
    return () => window.clearTimeout(timer);
  }, [animeId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const episodes = useMemo(() => buildEpisodes(anime?.episodes || 0), [anime?.episodes]);
  const currentEp = episodes.find((ep) => ep.no === selectedEp) || episodes[0];

  const related = useMemo(() => {
    if (!anime) return [];
    const sameCategory = animeList.filter(
      (item) => item.id !== anime.id && item.categories.some((cat) => anime.categories.includes(cat)),
    );
    const rest = animeList.filter((item) => item.id !== anime.id && !sameCategory.includes(item));
    return sortAnime([...sameCategory, ...rest], "popular").slice(0, 6);
  }, [anime]);

  const mainCategory = anime ? categories.find((cat) => cat.name === anime.categories[0]) : undefined;

  usePageMeta(
    anime ? `${anime.title} - 高清动漫在线观看 - 动漫免费在观看` : "作品未找到 - 动漫免费在观看",
    anime
      ? `${anime.summary.slice(0, 80)}本页提供${anime.title}的类型、地区、年份、更新状态与演示集数列表。`
      : "没有找到对应的动漫作品，可以返回首页或浏览全部动漫。",
    anime
      ? `${anime.title},${anime.categories.join(",")},${anime.region}动漫,高清动漫在线观看,免费观看动漫`
      : "动漫在线观看,免费观看动漫,高清动漫",
  );

  if (!anime) {
    return (
      <AppLayout>
        <div className="px-4 md:px-6 pt-5">
          <Breadcrumb items={[{ label: "作品详情" }]} />
          <div className="mt-6">
            <EmptyState
              icon="ri-file-unknow-line"
              title="没有找到这部动漫"
              description="这个链接对应的作品可能已被移除，或者编号有误。可以回到首页继续浏览。"
            >
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Link
                  to="/"
                  className="h-9 px-4 rounded-md bg-primary-500 text-background-50 text-[13px] flex items-center gap-1.5 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-5-line" />
                  回到首页
                </Link>
                <Link
                  to="/list"
                  className="h-9 px-4 rounded-md border border-background-300 text-foreground-800 text-[13px] flex items-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部动漫
                </Link>
              </div>
            </EmptyState>
          </div>
        </div>
        <SiteFooter />
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="px-4 md:px-6 pt-5">
          <div className="h-4 w-48 rounded bg-background-200 animate-soft-pulse" />
          <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 flex flex-col md:flex-row gap-5">
            <div className="w-36 md:w-44 aspect-[5/7] rounded-lg bg-background-200 animate-soft-pulse shrink-0" />
            <div className="flex-1 space-y-3">
              <div className="h-7 w-2/3 rounded bg-background-200 animate-soft-pulse" />
              <div className="h-4 w-1/2 rounded bg-background-200 animate-soft-pulse" />
              <div className="h-4 w-full rounded bg-background-200 animate-soft-pulse" />
              <div className="h-4 w-5/6 rounded bg-background-200 animate-soft-pulse" />
            </div>
          </div>
          <p className="mt-6 text-center text-[12px] text-foreground-500 flex items-center justify-center gap-1.5">
            <i className="ri-loader-4-line animate-spin" />
            正在加载作品信息…
          </p>
        </div>
      </AppLayout>
    );
  }

  const favorited = isFavorite(anime.id);
  const visibleTags = showAllTags ? anime.categories : anime.categories.slice(0, 3);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制（分享功能为演示）");
    } catch {
      setToast("分享功能为演示，暂未真实发送");
    }
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 pt-5">
        <Breadcrumb
          items={[
            { label: "分类", to: "/list" },
            mainCategory ? { label: `${mainCategory.name}动漫`, to: `/category/${mainCategory.id}` } : { label: "全部动漫", to: "/list" },
            { label: anime.title },
          ]}
        />

        <section className="mt-3 rounded-lg border border-background-200 bg-background-100 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="relative w-full md:w-[230px] shrink-0 bg-background-200">
              <div className="w-full aspect-[5/7] md:h-full">
                <img
                  src={anime.cover}
                  alt={`${anime.title} 大尺寸封面海报`}
                  title={`${anime.title} - 高清动漫在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <span className="absolute left-3 top-3 px-2 py-0.5 rounded bg-primary-950/80 text-background-50 text-[11px]">
                {anime.status}
              </span>
            </div>

            <div className="flex-1 px-5 py-5 md:px-7 md:py-6">
              <h1 className="font-heading text-xl md:text-[26px] font-bold text-foreground-950 leading-tight">
                {anime.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-foreground-600">
                <span className="flex items-center gap-1 text-accent-600 font-semibold">
                  <i className="ri-star-fill" />
                  {anime.rating.toFixed(1)}
                </span>
                <span>{anime.year}</span>
                <span>{anime.region}</span>
                <span>{anime.studio}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line" />
                  {formatUpdated(anime.updatedAt)} 更新
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {visibleTags.map((tag) => {
                  const cat = categories.find((item) => item.name === tag);
                  return (
                    <Link
                      key={tag}
                      to={cat ? `/category/${cat.id}` : "/list"}
                      className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {tag}
                    </Link>
                  );
                })}
                {anime.categories.length > 3 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllTags((prev) => !prev)}
                    className="px-2 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] cursor-pointer whitespace-nowrap"
                  >
                    {showAllTags ? "收起" : `+${anime.categories.length - 3}`}
                  </button>
                ) : null}
              </div>

              <p className="mt-4 text-[13px] leading-[1.85] text-foreground-700">{anime.summary}</p>

              <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "类型", value: anime.categories.slice(0, 2).join(" / ") },
                  { label: "地区", value: anime.region },
                  { label: "年份", value: `${anime.year}` },
                  { label: "总集数", value: `${anime.episodes} 集` },
                ].map((item) => (
                  <div key={item.label} className="rounded-md bg-background-50 border border-background-200 px-3 py-2">
                    <dt className="text-[11px] text-foreground-500">{item.label}</dt>
                    <dd className="mt-0.5 text-[12.5px] text-foreground-900 font-medium truncate">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    document.getElementById("episode-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    setToast("集数为演示界面，暂不提供真实播放");
                  }}
                  className="h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-[13px] font-semibold flex items-center gap-1.5 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-base" />
                  选择集数
                </button>
                <button
                  type="button"
                  onClick={() => toggleFavorite(anime.id)}
                  className={`h-10 px-4 rounded-md text-[13px] flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "border border-background-300 text-foreground-800 hover:bg-background-50"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {favorited ? "已加入收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="h-10 px-4 rounded-md border border-background-300 text-foreground-800 text-[13px] flex items-center gap-1.5 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="h-10 w-10 rounded-md border border-background-300 text-foreground-700 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
                  aria-label="返回上一页"
                >
                  <i className="ri-arrow-left-line" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
          <div id="episode-section" className="scroll-mt-6">
            <EpisodeList
              episodes={episodes}
              selected={selectedEp}
              onSelect={setSelectedEp}
              currentEp={currentEp}
              totalEpisodes={anime.episodes}
            />
          </div>

          <aside className="flex flex-col gap-5">
            <section className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                <i className="ri-mic-line text-accent-500" />
                制作与声优
              </h2>
              <div className="mt-3 space-y-2 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">制作公司</span>
                  <span className="text-foreground-900">{anime.studio}</span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-foreground-500 shrink-0">主要声优</span>
                  <span className="text-foreground-900 text-right">{anime.voiceCast.join(" / ")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">播放热度</span>
                  <span className="text-foreground-900">{anime.popularity.toLocaleString()}</span>
                </div>
              </div>
            </section>

            <section className="rounded-lg bg-primary-950 p-4 text-background-200">
              <h2 className="font-heading text-sm font-bold text-background-50 flex items-center gap-1.5">
                <i className="ri-information-line text-accent-400" />
                演示说明
              </h2>
              <p className="mt-2 text-[11.5px] leading-relaxed">
                本页集数列表仅用于展示选集界面结构，点击集数只会切换集数简介，
                <strong className="text-accent-400">不包含真实视频播放、缓存或下载</strong>。
              </p>
              <Link
                to="/about"
                className="mt-3 inline-flex items-center gap-1 text-[11.5px] text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多
                <i className="ri-arrow-right-s-line" />
              </Link>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                <i className="ri-links-line text-accent-500" />
                分类入口
              </h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {categories
                  .filter((cat) => anime.categories.includes(cat.name))
                  .map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className={cat.icon} />
                      {cat.name}动漫
                    </Link>
                  ))}
                <Link
                  to="/list"
                  className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  全部动漫
                </Link>
              </div>
            </section>
          </aside>
        </div>

        <section className="mt-8" aria-labelledby="related-title">
          <div className="flex items-end justify-between gap-3">
            <h2 id="related-title" className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              相关推荐
            </h2>
            <Link
              to={mainCategory ? `/category/${mainCategory.id}` : "/list"}
              className="text-[12px] text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
            >
              更多{mainCategory ? `${mainCategory.name}动漫` : "作品"}
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-3 gap-y-5">
            {related.map((item) => (
              <AnimeCard key={item.id} anime={item} size="sm" />
            ))}
          </div>
        </section>
      </div>

      {toast ? (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-[12.5px] animate-fade-up whitespace-nowrap">
          {toast}
        </div>
      ) : null}
      <SiteFooter />
    </AppLayout>
  );
}